import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, unref as _unref, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-93bf35ac"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["for"]
const _hoisted_2 = {
  key: 0,
  class: "text-sm text-gray-500 my-4"
}
const _hoisted_3 = ["value", "id", "name", "type", "placeholder", "autocomplete", "disabled", "maxlength"]
const _hoisted_4 = {
  key: 1,
  class: "hidden lg:block"
}
const _hoisted_5 = {
  key: 0,
  class: "mt-2 text-sm text-red-600"
}

import { Component, computed, onMounted, ref, watch, watchEffect, withDefaults } from "vue"
import GgmsTooltip from "@/components/GgmsTooltip.vue"
import { ExclamationCircleIcon } from "@heroicons/vue/solid"
import { DuplicateIcon } from "@heroicons/vue/outline"
import { copyToClipboard } from "@/shared/utils/helpers"

interface Props {
    modelValue?: string | number
    label?: string
    description?: string
    name?: string
    type?: string
    required?: boolean
    autocomplete?: string
    placeholder?: string
    iconStart?: Component
    iconEnd?: Component
    inline?: boolean
    invalid?: boolean
    small?: boolean
    errorMessage?: string
    styleType?: "primary" | "secondary"
    disabled?: boolean
    textAlign?: string
    inputClass?: string
    wrapperClass?: string
    isAvailableDateFromPast?: boolean
    templateString?: string
    readonly?: boolean
    isFocusExposed?: boolean
    maxLength?: number
    showRequired?: boolean
    showCopyButton?: boolean
    fullWidth?: boolean
    overwriteShowErrors?: boolean
    showIconEnd?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsInput',
  props: {
    modelValue: {},
    label: {},
    description: {},
    name: {},
    type: { default: "text" },
    required: { type: Boolean, default: false },
    autocomplete: {},
    placeholder: {},
    iconStart: {},
    iconEnd: {},
    inline: { type: Boolean },
    invalid: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    errorMessage: {},
    styleType: { default: "primary" },
    disabled: { type: Boolean, default: false },
    textAlign: { default: "start" },
    inputClass: { default: "" },
    wrapperClass: {},
    isAvailableDateFromPast: { type: Boolean, default: false },
    templateString: { default: "" },
    readonly: { type: Boolean },
    isFocusExposed: { type: Boolean },
    maxLength: {},
    showRequired: { type: Boolean, default: true },
    showCopyButton: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: true },
    overwriteShowErrors: { type: Boolean, default: false },
    showIconEnd: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "clickIconStart", "clickIconEnd"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit

const props = __props

const showErrors = ref(true)
const localErrorMessage = ref(props.errorMessage)
const localInvalid = ref(props.invalid)

const baseStyle = computed(() => [
    props.iconStart ? "pl-10" : "",
    props.iconEnd ? "pr-10" : "",
    props.inline ? "w-80" : "",
    props.small ? "text-sm" : "text-base",
    props.fullWidth ? "w-full" : "",
    "appearance-none block px-3 py-2 border rounded-md shadow-sm",
    props.inputClass,
    `text-${props.textAlign}`,
])

const inputStyle = computed(() => [
    ...baseStyle.value,
    props.disabled ? "opacity-50 cursor-not-allowed" : "",
    props.styleType === "secondary" ? "bg-gray-100 border-none focus:border-gray-500" : "",
    localInvalid.value && showErrors.value
        ? "pr-10 border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500"
        : "placeholder-gray-400 focus:ring-primary-color-500 focus:border-primary-color-500 border-gray-300 ",
    "focus:outline-none",
])

const readonlyStyle = computed(() => [
    ...baseStyle.value,
    "opacity-50 cursor-not-allowed",
    props.styleType === "secondary" ? "bg-gray-200 border-none" : "bg-gray-100",
    "border-gray-300 placeholder-gray-400",
])

const starStyle = computed(() => [localInvalid.value ? "text-red-600" : "text-gray-700", "pl-1"])
const getRequiredStyleInline = computed(() => (props.inline ? "gap-2" : ""))
const exclamationMarkStyle = computed(() => [
    props.showCopyButton ? "pr-8" : "pr-3",
    "absolute inset-y-0 right-0 flex items-center",
])

const inputField = ref()

function getInlineLabelStyle() {
    return [props.inline ? "w-2/5" : "", "h-6"]
}

function getWrapperStyleInline() {
    return [props.inline ? "flex items-center gap-4" : "", props.wrapperClass]
}

function getInputWrapperStyleLabel() {
    return ["relative flex items-center justify-center"]
}

function getIconsDisabledClass() {
    return props.disabled ? "cursor-default" : "cursor-pointer"
}

function disableValidationError() {
    localErrorMessage.value = ""
    localInvalid.value = false
    if (props.overwriteShowErrors) {
        showErrors.value = false
    }
}

function onChangeInput() {
    disableValidationError()
    emit("update:modelValue", inputField.value.value)
}

function onSelectInsertString(templateString: string) {
    const cursorPosition = inputField.value.selectionStart
    const textAreaValue = inputField.value.value
    const textBefore = textAreaValue.substring(0, cursorPosition)
    const textAfter = textAreaValue.substring(cursorPosition, textAreaValue.length)
    inputField.value.value = `${textBefore}${templateString}${textAfter}`
    inputField.value.focus()
    inputField.value.selectionStart = cursorPosition + templateString.length
    inputField.value.selectionEnd = cursorPosition + templateString.length
    emit("update:modelValue", inputField.value.value)
}

function focusInputElement() {
    inputField.value.focus()
}

onMounted(() => {
    disableValidationError()
    //check if datepicker is open
    if (props.type !== "date" || !props.isFocusExposed) {
        return
    }
    inputField.value?.addEventListener("focus", () => {
        try {
            inputField?.value?.showPicker()
        } catch (error) {
            console.error(error)
        }
    })
})

watchEffect(() => {
    if (props.type === "date" && inputField.value && !props.isAvailableDateFromPast) {
        inputField.value.min = new Date().toISOString().split("T")[0]
    }
})

watch(
    () => props.errorMessage,
    (newVal) => {
        localErrorMessage.value = newVal
        localInvalid.value = props.invalid
    }
)

watch(
    () => props.invalid,
    () => {
        localInvalid.value = props.invalid
    }
)

watch(
    () => props.templateString,
    (val) => {
        onSelectInsertString(val)
    }
)

watch(
    () => props.modelValue,
    () => {
        if (!props.modelValue && props.invalid) {
            localErrorMessage.value = props.errorMessage
            localInvalid.value = props.invalid
        }
    }
)

__expose({
    focusInputElement,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      class: _normalizeClass([getWrapperStyleInline()])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["flex", getRequiredStyleInline.value])
      }, [
        (_ctx.label || _ctx.inline)
          ? (_openBlock(), _createElementBlock("p", {
              key: 0,
              for: _ctx.name,
              class: _normalizeClass(["text-sm font-medium text-gray-700", getInlineLabelStyle()])
            }, _toDisplayString(_ctx.label), 11, _hoisted_1))
          : _createCommentVNode("", true),
        (_ctx.required && _ctx.showRequired)
          ? (_openBlock(), _createElementBlock("span", {
              key: 1,
              class: _normalizeClass(starStyle.value)
            }, "*", 2))
          : _createCommentVNode("", true)
      ], 2),
      (_ctx.description)
        ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_ctx.description), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(getInputWrapperStyleLabel())
      }, [
        _renderSlot(_ctx.$slots, "input", {}, () => [
          (_ctx.iconStart)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["absolute inset-y-0 left-0 pl-3 flex items-center", getIconsDisabledClass()]),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('clickIconStart')))
              }, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.iconStart), {
                  class: "h-5 w-5 text-gray-400",
                  "aria-hidden": "true"
                }))
              ], 2))
            : _createCommentVNode("", true),
          _createElementVNode("input", {
            ref_key: "inputField",
            ref: inputField,
            value: _ctx.modelValue,
            id: _ctx.name,
            name: _ctx.name,
            type: _ctx.type,
            placeholder: _ctx.placeholder,
            autocomplete: _ctx.autocomplete,
            disabled: _ctx.disabled || _ctx.readonly,
            maxlength: _ctx.maxLength,
            class: _normalizeClass(_ctx.readonly ? readonlyStyle.value : inputStyle.value),
            onInput: onChangeInput,
            onBlur: _cache[1] || (_cache[1] = ($event: any) => (showErrors.value = true))
          }, null, 42, _hoisted_3),
          (_ctx.iconEnd || localInvalid.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: _normalizeClass([getIconsDisabledClass(), exclamationMarkStyle.value]),
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('clickIconEnd')))
              }, [
                (localInvalid.value && showErrors.value)
                  ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.iconEnd || _unref(ExclamationCircleIcon)), {
                      key: 0,
                      class: _normalizeClass([localInvalid.value && showErrors.value ? 'text-red-600' : 'text-gray-400', 'h-5 w-5']),
                      "aria-hidden": "true"
                    }, null, 8, ["class"]))
                  : (_ctx.showIconEnd)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.iconEnd), {
                        key: 1,
                        class: "h-5 w-5 text-gray-400",
                        "aria-hidden": "true"
                      }))
                    : _createCommentVNode("", true)
              ], 2))
            : _createCommentVNode("", true)
        ]),
        (_ctx.showCopyButton)
          ? (_openBlock(), _createBlock(GgmsTooltip, {
              key: 0,
              text: "Copy Text",
              icon: _unref(DuplicateIcon),
              class: "ml-1",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(copyToClipboard)(_ctx.modelValue as string)))
            }, null, 8, ["icon"]))
          : _createCommentVNode("", true)
      ], 2),
      (_ctx.inline)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4))
        : _createCommentVNode("", true)
    ], 2),
    (localErrorMessage.value && showErrors.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(localErrorMessage.value), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})